import { isProduction } from 'shared/constants/AppTypeConstants';
import { useEffect, useState } from 'react';
import { loadScript } from 'shared/helpers/BrowserHelpers';

const BOTPRESS_INJECT_URL = 'https://cdn.botpress.cloud/webchat/v3.6/inject.js';
const BOTPRESS_CONFIG_URL = isProduction
  ? 'https://files.bpcontent.cloud/2025/09/11/19/20250911191250-2YBAJIVJ.js'
  : 'https://files.bpcontent.cloud/2025/10/06/18/20251006183518-CMQYMOBR.js';

let resolveBotpress: (value: NonNullable<typeof window.botpress>) => void;
const botpressPromise = new Promise<NonNullable<typeof window.botpress>>(
  (resolve) => {
    resolveBotpress = resolve;
  },
);

let loadPromise:
  | Promise<NonNullable<typeof window.botpress> | undefined>
  | undefined;

export function getBotpress() {
  return botpressPromise;
}

export function loadBotpress() {
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    await loadScript({ src: BOTPRESS_INJECT_URL });
    await loadScript({ src: BOTPRESS_CONFIG_URL });

    const { botpress } = window;
    if (!botpress) return undefined;

    if (botpress.initialized) {
      resolveBotpress(botpress);
      return botpress;
    }

    return new Promise<NonNullable<typeof window.botpress>>((resolve) => {
      const unsubscribe = botpress.on('webchat:initialized', () => {
        resolveBotpress(botpress);
        unsubscribe();
        resolve(botpress);
      });
    });
  })();

  return loadPromise;
}

/**
 * Hides the greeting bubble and turns off proactive messages so chat only
 * opens on user action. Callers decide the scope — not applied globally.
 */
export function disableProactiveGreeting(
  botpress: NonNullable<typeof window.botpress>,
) {
  botpress.hideMessagePreview();
  botpress.config({
    configuration: {
      ...botpress.configuration,
      proactiveMessageEnabled: false,
    },
  });
}

/** Restore the proactive greeting when leaving a page that suppressed it. */
export function enableProactiveGreeting(
  botpress: NonNullable<typeof window.botpress>,
) {
  botpress.config({
    configuration: {
      ...botpress.configuration,
      proactiveMessageEnabled: true,
    },
  });
}

export function hideDefaultBotpressFab() {
  const elements = document.getElementsByClassName(
    'bpChatContainer',
  ) as HTMLCollectionOf<HTMLElement>;

  for (const element of elements) {
    element.style.display = 'none';
    element.style.visibility = 'hidden';
  }
}

export function showDefaultBotpressFab() {
  const elements = document.getElementsByClassName(
    'bpChatContainer',
  ) as HTMLCollectionOf<HTMLElement>;

  for (const element of elements) {
    element.style.display = 'block';
    element.style.visibility = 'visible';
  }
}

export function useBotpress() {
  const [botpress, setBotpress] = useState<typeof window.botpress>();

  useEffect(() => {
    if ('Cypress' in window) return;
    if (import.meta.env.NODE_ENV === 'test') return;
    void getBotpress().then(setBotpress);
  }, []);

  return botpress;
}
